const REQUIRED_KEYS = [
  "reportTitle",
  "reportSubtitle",
  "reportDescription",
  "reportMeta",
  "basicReportData",
  "numericalResults",
  "analysis",
  "recommendationsAndAlerts",
];

const SUPPORTED_REPORT_META_KEYS = ["period", "currency", "customer", "preparedBy", "custom"];

const isEnglishLanguage = (language) =>
  ["en", "english"].includes(String(language || "").toLowerCase());

const getDefaultNumericalColumns = (language) =>
  isEnglishLanguage(language)
    ? {
        item: "Item",
        metric: "Metric",
        value: "Value",
        change: "Change",
      }
    : {
        item: "البند",
        metric: "النسبة",
        value: "القيمة",
        change: "التغير",
      };

const toNullableString = (value) => {
  if (value === null || value === undefined) return null;
  const text = String(value).trim();
  return text ? text : null;
};

const toDisplayString = (value) => {
  if (value === null || value === undefined) return "";
  return String(value).trim();
};

const inferReportMetaKey = (entry = {}) => {
  const rawKey = toNullableString(entry?.key);
  if (rawKey && SUPPORTED_REPORT_META_KEYS.includes(rawKey)) {
    return rawKey;
  }

  const label = String(entry?.label || "").trim().toLowerCase();

  if (["period", "date", "month", "year", "time", "الفترة", "التاريخ", "الشهر", "السنة", "الوقت"].some((item) => label.includes(item))) {
    return "period";
  }

  if (["currency", "curr", "العملة"].some((item) => label.includes(item))) {
    return "currency";
  }

  if (["customer", "client", "account", "العميل", "الزبون"].some((item) => label.includes(item))) {
    return "customer";
  }

  if (["prepared by", "preparedby", "author", "user", "manager", "owner", "أعد", "اسم", "المستخدم", "المدير"].some((item) => label.includes(item))) {
    return "preparedBy";
  }

  return "custom";
};

const normalizeKeyValueRows = (value) => {
  if (!Array.isArray(value)) return [];

  return value
    .map((entry) => ({
      key: inferReportMetaKey(entry),
      label: toDisplayString(entry?.label),
      value: toDisplayString(entry?.value),
    }))
    .filter((entry) => entry.label && entry.value);
};

const normalizeNumericalRows = (value) => {
  if (!Array.isArray(value)) return [];

  return value
    .map((row) => ({
      item: toDisplayString(row?.item),
      metric: toDisplayString(row?.metric),
      value: toDisplayString(row?.value),
      change: toDisplayString(row?.change),
    }))
    .filter((row) => row.item || row.metric || row.value || row.change);
};

const normalizeNumericalResults = (value, language) => {
  if (value === null) return null;
  if (typeof value !== "object" || Array.isArray(value)) return null;

  const defaults = getDefaultNumericalColumns(language);

  return {
    description: toNullableString(value.description),
    columns: {
      item: toDisplayString(value.columns?.item) || defaults.item,
      metric: toDisplayString(value.columns?.metric) || defaults.metric,
      value: toDisplayString(value.columns?.value) || defaults.value,
      change: toDisplayString(value.columns?.change) || defaults.change,
    },
    rows: normalizeNumericalRows(value.rows),
  };
};

const normalizeTextList = (value) => {
  if (!Array.isArray(value)) return [];

  return value
    .map((entry) => toNullableString(entry))
    .filter(Boolean);
};

const normalizeLegacyTextList = (value) => {
  if (!Array.isArray(value)) return [];

  return value
    .map((entry) => {
      if (typeof entry === "string") return toNullableString(entry);
      if (entry && typeof entry === "object") {
        return toNullableString(entry.message || entry.text || entry.description || entry.value);
      }
      return null;
    })
    .filter(Boolean);
};

const normalizeComparableText = (value) => String(value || "").trim().replace(/\s+/g, " ").toLowerCase();

const removeDuplicateText = (items, primaryText) => {
  const primaryKey = normalizeComparableText(primaryText);
  const seen = new Set(primaryKey ? [primaryKey] : []);

  return items.filter((item) => {
    const key = normalizeComparableText(item);
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const getMissingKeys = (value) => {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return REQUIRED_KEYS;
  }

  return REQUIRED_KEYS.filter((key) => !(key in value));
};

const tryParseJson = (candidate = "") => {
  try {
    return {
      success: true,
      value: JSON.parse(candidate),
      error: null,
    };
  } catch (error) {
    return {
      success: false,
      value: null,
      error,
    };
  }
};

const collectJsonCandidates = (rawResponse = "") => {
  const response = String(rawResponse || "").trim();
  const candidates = [];

  if (!response) return candidates;

  candidates.push(response);

  const fencedMatches = response.matchAll(/```json\s*([\s\S]*?)\s*```/gi);
  Array.from(fencedMatches).forEach((match) => {
    if (match?.[1]) candidates.push(match[1].trim());
  });

  const firstBrace = response.indexOf("{");
  const lastBrace = response.lastIndexOf("}");
  if (firstBrace >= 0 && lastBrace > firstBrace) {
    candidates.push(response.slice(firstBrace, lastBrace + 1).trim());
  }

  return Array.from(new Set(candidates.filter(Boolean)));
};

const selectJsonCandidate = (rawResponse = "") => {
  const candidates = collectJsonCandidates(rawResponse);
  const parsedCandidates = candidates.map((candidate) => ({
    candidate,
    parsed: tryParseJson(candidate),
  }));

  const contractMatch = parsedCandidates.find(({ parsed }) => {
    if (!parsed.success) return false;
    return getMissingKeys(parsed.value).length === 0;
  });

  if (contractMatch) {
    return {
      candidate: contractMatch.candidate,
      parsed: contractMatch.parsed.value,
      parseError: null,
      parsedCandidates,
      strategy: "contract-match",
    };
  }

  const legacyConflict = parsedCandidates.find(({ parsed }) => {
    if (!parsed.success || !parsed.value || typeof parsed.value !== "object") {
      return false;
    }

    return (
      parsed.value.status === "INPUT_CONFLICT" ||
      "financial_calculations" in parsed.value ||
      ("recommendations" in parsed.value && "notes" in parsed.value)
    );
  });

  if (legacyConflict) {
    return {
      candidate: legacyConflict.candidate,
      parsed: legacyConflict.parsed.value,
      parseError: null,
      parsedCandidates,
      strategy: "legacy-conflict-schema",
    };
  }

  const firstParsedObject = parsedCandidates.find(
    ({ parsed }) => parsed.success && parsed.value && typeof parsed.value === "object"
  );

  if (firstParsedObject) {
    return {
      candidate: firstParsedObject.candidate,
      parsed: firstParsedObject.parsed.value,
      parseError: null,
      parsedCandidates,
      strategy: "first-json-object",
    };
  }

  return {
    candidate: candidates[0] || "",
    parsed: null,
    parseError: parsedCandidates.find(({ parsed }) => !parsed.success)?.parsed?.error || null,
    parsedCandidates,
    strategy: "no-json-object",
  };
};

const findParsedSection = (parsedCandidates = [], predicate) =>
  parsedCandidates
    .map(({ parsed }) => (parsed.success ? parsed.value : null))
    .find((value) => value && typeof value === "object" && !Array.isArray(value) && predicate(value)) || null;

const buildLegacyReportMeta = ({ customerContext, filters, language, reportContext }) => {
  const isEnglish = isEnglishLanguage(language);
  const period = toNullableString(reportContext?.period || filters?.period || filters?.periodFrom || filters?.periodTo);
  const currency = toNullableString(reportContext?.currency || filters?.currency);
  const customer = toNullableString(reportContext?.customerContext?.name || customerContext?.name);

  return [
    period
      ? {
          key: "period",
          label: isEnglish ? "Period" : "الفترة",
          value: period,
        }
      : null,
    currency
      ? {
          key: "currency",
          label: isEnglish ? "Currency" : "العملة",
          value: currency,
        }
      : null,
    customer
      ? {
          key: "customer",
          label: isEnglish ? "Customer" : "العميل",
          value: customer,
        }
      : null,
  ].filter(Boolean);
};

// The assistant only signals a genuine "cannot produce the report" with these statuses.
const CONFLICT_STATUSES = new Set([
  "INPUT_CONFLICT",
  "INSUFFICIENT",
  "INSUFFICIENT_DATA",
  "ERROR",
  "FAILED",
  "FAILURE",
]);

// The assistant's financial_calculations array shape varies by report type; map the
// common field names to the display's { item, metric, value, change } columns.
const buildCalculationRows = (calculations) => {
  if (!Array.isArray(calculations)) return [];

  return calculations
    .map((entry) => {
      if (!entry || typeof entry !== "object") return null;
      const item = toDisplayString(
        entry.item_name ?? entry.item ?? entry.name ?? entry.label ?? entry.metric_name ?? entry.account ?? entry.title
      );
      const metric = toDisplayString(
        entry.classification ?? entry.metric ?? entry.category ?? entry.type ?? entry.indicator ?? entry.status
      );
      const value = toDisplayString(
        entry.current_amount ?? entry.value ?? entry.amount ?? entry.result ?? entry.ratio ?? entry.current ?? entry.current_value
      );
      const change = toDisplayString(
        entry.percentage_change !== undefined && entry.percentage_change !== null
          ? `${entry.percentage_change}%`
          : entry.absolute_change ?? entry.change ?? entry.variance ?? entry.delta ?? ""
      );
      return { item, metric, value, change };
    })
    .filter((row) => row && (row.item || row.metric || row.value || row.change));
};

// Adapts the assistant's actual output schema
// { status, financial_calculations, analysis, recommendations, notes } into the
// unified report view model. A real SUCCESS is rendered as a full report; only a
// genuine conflict/error status falls back to the "unable to complete" card.
const buildAssistantReport = ({ language, parsed, parsedCandidates }) => {
  const isEnglish = isEnglishLanguage(language);
  const reportContext = findParsedSection(
    parsedCandidates,
    (value) => "reportType" in value && ("reportKind" in value || "outputLanguage" in value)
  );
  const filters = findParsedSection(
    parsedCandidates,
    (value) => "hasUploadedExcel" in value || "analysisType" in value || "periodFrom" in value
  );
  const customerContext =
    findParsedSection(
      parsedCandidates,
      (value) => "name" in value && !("reportType" in value) && !("sections" in value)
    ) || reportContext?.customerContext || null;

  const reportMeta = buildLegacyReportMeta({ customerContext, filters, language, reportContext });
  const notes = normalizeLegacyTextList(parsed.notes);
  const analysisList = normalizeLegacyTextList(parsed.analysis);
  const recommendationsList = normalizeLegacyTextList(parsed.recommendations);
  const numericalRows = buildCalculationRows(parsed.financial_calculations);
  const status = String(parsed.status || "").trim().toUpperCase();
  const statusRow = parsed.status
    ? [{ key: "custom", label: isEnglish ? "Status" : "الحالة", value: toDisplayString(parsed.status) }]
    : [];

  const isConflict =
    CONFLICT_STATUSES.has(status) || (numericalRows.length === 0 && analysisList.length === 0);

  if (isConflict) {
    const mainMessage =
      notes[0] ||
      analysisList[0] ||
      (isEnglish
        ? "The available data cannot support the requested report."
        : "البيانات المتاحة لا تكفي لإعداد التقرير المطلوب.");
    return {
      reportTitle: isEnglish ? "Unable to complete the requested report" : "تعذر إعداد التقرير المطلوب",
      reportSubtitle: isEnglish ? "Insufficient or conflicting input data" : "البيانات غير كافية أو غير متوافقة",
      reportDescription: mainMessage,
      reportMeta,
      basicReportData: statusRow,
      numericalResults: null,
      analysis: removeDuplicateText(analysisList, mainMessage),
      recommendationsAndAlerts: removeDuplicateText(recommendationsList, mainMessage),
    };
  }

  return {
    reportTitle: isEnglish ? "Financial Analysis Report" : "تقرير التحليل المالي",
    reportSubtitle: null,
    reportDescription: notes[0] || null,
    reportMeta,
    basicReportData: statusRow,
    numericalResults: numericalRows.length
      ? { description: notes[1] || null, columns: getDefaultNumericalColumns(language), rows: numericalRows }
      : null,
    analysis: analysisList,
    recommendationsAndAlerts: recommendationsList,
  };
};

export const buildJsonContractExample = (language = "Arabic") => {
  const isEnglish = isEnglishLanguage(language);

  return {
    reportTitle: isEnglish ? "<Report title based on the actual report>" : "<عنوان التقرير بناءً على التقرير الفعلي>",
    reportSubtitle: isEnglish
      ? "<Optional subtitle based on the actual report>"
      : "<عنوان فرعي اختياري بناءً على التقرير الفعلي>",
    reportDescription: isEnglish
      ? "<Optional one-line description based only on supplied data>"
      : "<وصف اختياري من سطر واحد مبني فقط على البيانات المرسلة>",
    reportMeta: [
      {
        key: "period",
        label: isEnglish ? "Period" : "الفترة",
        value: isEnglish ? "<Period value>" : "<قيمة الفترة>",
      },
      {
        key: "currency",
        label: isEnglish ? "Currency" : "العملة",
        value: isEnglish ? "<Currency value>" : "<قيمة العملة>",
      },
      {
        key: "customer",
        label: isEnglish ? "Customer" : "العميل",
        value: isEnglish ? "<Customer name>" : "<اسم العميل>",
      },
      {
        key: "preparedBy",
        label: isEnglish ? "Prepared By" : "إعداد",
        value: isEnglish ? "<Prepared by>" : "<اسم المسؤول>",
      },
    ],
    basicReportData: [
      {
        label: isEnglish ? "<Data label>" : "<عنوان البيان>",
        value: isEnglish ? "<Data value>" : "<قيمة البيان>",
      },
    ],
    numericalResults: {
      description: isEnglish
        ? "<Optional short note for the numerical section>"
        : "<ملاحظة قصيرة اختيارية لقسم النتائج الرقمية>",
      columns: {
        item: isEnglish ? "<Item column title>" : "<عنوان عمود البند>",
        metric: isEnglish ? "<Metric column title>" : "<عنوان عمود النسبة>",
        value: isEnglish ? "<Value column title>" : "<عنوان عمود القيمة>",
        change: isEnglish ? "<Change column title>" : "<عنوان عمود التغير>",
      },
      rows: [
        {
          item: isEnglish ? "<Row item>" : "<اسم البند>",
          metric: isEnglish ? "<Metric text>" : "<النسبة أو الوصف>",
          value: isEnglish ? "<Formatted value>" : "<القيمة المنسقة>",
          change: isEnglish ? "<Change text>" : "<التغير المنسق>",
        },
      ],
    },
    analysis: [
      isEnglish
        ? "<Analysis point based on actual report data>"
        : "<نقطة تحليلية مبنية على بيانات التقرير الفعلية>",
    ],
    recommendationsAndAlerts: [
      isEnglish
        ? "<Recommendation or alert based on actual report data>"
        : "<توصية أو تنبيه مبني على بيانات التقرير الفعلية>",
    ],
  };
};

export const parseAiJsonReport = (rawResponse = "", language = "Arabic") => {
  const selection = selectJsonCandidate(rawResponse);
  if (!selection.candidate) {
    return {
      success: false,
      errorMessage: "AI response is empty.",
      validation: {
        strategy: "empty-response",
        missingKeys: REQUIRED_KEYS,
      },
    };
  }

  if (!selection.parsed) {
    return {
      success: false,
      errorMessage: "AI response is not valid JSON.",
      validation: {
        strategy: selection.strategy || "invalid-json",
        parseError: selection.parseError?.message || "No valid JSON object found.",
        missingKeys: REQUIRED_KEYS,
      },
    };
  }

  const parsed = selection.parsed;

  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
    return {
      success: false,
      errorMessage: "AI response must be a JSON object.",
      validation: {
        strategy: "invalid-root",
        missingKeys: REQUIRED_KEYS,
      },
    };
  }

  const missingKeys = getMissingKeys(parsed);
  if (missingKeys.length > 0) {
    if (selection.strategy === "legacy-conflict-schema") {
      const report = buildAssistantReport({
        language,
        parsed,
        parsedCandidates: selection.parsedCandidates,
      });

      return {
        success: true,
        report,
        validation: {
          strategy: "assistant-schema-adapter",
          missingKeys: [],
          receivedKeys: Object.keys(parsed),
          hasContent: true,
        },
      };
    }

    return {
      success: false,
      errorMessage:
        selection.strategy === "legacy-conflict-schema"
          ? "AI returned a legacy conflict response instead of the unified report JSON contract."
          : "AI response does not match the required JSON contract.",
      validation: {
        strategy: selection.strategy === "legacy-conflict-schema" ? "legacy-conflict-schema" : "missing-keys",
        missingKeys,
        receivedKeys: Object.keys(parsed),
      },
    };
  }

  const report = {
    reportTitle: toNullableString(parsed.reportTitle),
    reportSubtitle: toNullableString(parsed.reportSubtitle),
    reportDescription: toNullableString(parsed.reportDescription),
    reportMeta: normalizeKeyValueRows(parsed.reportMeta),
    basicReportData: normalizeKeyValueRows(parsed.basicReportData),
    numericalResults: normalizeNumericalResults(parsed.numericalResults, language),
    analysis: normalizeTextList(parsed.analysis),
    recommendationsAndAlerts: normalizeTextList(parsed.recommendationsAndAlerts),
  };

  if (!report.reportTitle) {
    return {
      success: false,
      errorMessage: "AI response is missing a valid report title.",
      validation: {
        strategy: "invalid-title",
        missingKeys: ["reportTitle"],
      },
    };
  }

  return {
    success: true,
    report,
    validation: {
      strategy: "json-contract",
      missingKeys: [],
      hasContent: Boolean(
        report.basicReportData.length ||
          report.reportMeta.length ||
          report.numericalResults?.rows.length ||
          report.analysis.length ||
          report.recommendationsAndAlerts.length
      ),
    },
  };
};
